import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Post } from '../lib/types';
import { Halftone, type HalftoneType } from './Halftone';
import './PostCard.css';

interface Props {
  post: Post;
  featured?: boolean; // every 7th card full-width
  /** Force a uniform media aspect-ratio (e.g. "4 / 3" in the Archive grid) so
   *  the page doesn't have to override the media ratio with !important. */
  ratio?: string;
}

// deterministic halftone pick so a given post always renders the
// same texture (no flicker across renders / pagination).
const TEXT_HALFTONES: HalftoneType[] = [
  'bloom',
  'off-spotlight',
  'drift',
  'twin-bloom',
  'cloud',
  'edge-bloom',
  'tri-bloom',
  'whisper',
];
function hash(id: string): number {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return h;
}
function halftoneFor(id: string): HalftoneType {
  return TEXT_HALFTONES[hash(id) % TEXT_HALFTONES.length];
}
// "deliberately random, exciting" magazine rhythm - but mostly
// quiet: ~5 of 8 text tiles stay rectangular, the rest take a
// gentle non-box shape. Only safe shapes (no content clipping).
const TEXT_SHAPES = ['', '', '', 'shape-tilted', '', 'shape-arch', '', 'shape-diagonal'];
function shapeFor(id: string): string {
  return TEXT_SHAPES[hash(id) % TEXT_SHAPES.length];
}

export function PostCard({ post, featured, ratio }: Props) {
  // A cover URL can be present but dead (deleted storage object, un-processed
  // video). Track a load failure so we drop to the brand texture instead of a
  // broken-image icon.
  const [coverFailed, setCoverFailed] = useState(false);
  const drawerLabel = post.drawers?.[0]?.name ?? '';
  const mediaRatio = ratio ?? String(post.media?.aspect_ratio ?? 16 / 9);

  // An explicit per-post thumbnail wins over the primary-media cover.
  const cover = post.thumbnail_url ?? post.media?.thumbnail_url ?? null;

  const isText = post.post_type === 'text';

  return (
    <Link
      to={`/p/${post.slug}`}
      className={`post-card ${featured ? 'post-card--featured' : ''} ${isText ? 'post-card--text' : ''}`}
      aria-label={post.title}
    >
      {isText ? (
        // text post = the canonical halftone tile: quiet B&W frame,
        // accent-tinted texture, Carmen title carries it.
        <div
          className={`post-card__text-cover ${shapeFor(post.id)}`}
          style={ratio ? { aspectRatio: ratio } : undefined}
        >
          <span className="post-card__halftone" aria-hidden="true">
            <Halftone type={halftoneFor(post.id)} />
          </span>
          <div className="post-card__text-inner">
            {drawerLabel && <p className="post-card__drawer kicker">{drawerLabel}</p>}
            <h3 className="post-card__text-title">{post.title}</h3>
            {post.dek && <p className="post-card__text-dek">{post.dek}</p>}
          </div>
        </div>
      ) : (
        <>
          <div className="post-card__media" style={{ aspectRatio: mediaRatio }}>
            {cover && !coverFailed ? (
              <img src={cover} alt="" loading="lazy" onError={() => setCoverFailed(true)} />
            ) : (
              // no cover yet - fall back to the brand texture, not a flat block
              <div className="post-card__type-cover">
                <span className="post-card__halftone" aria-hidden="true">
                  <Halftone type={halftoneFor(post.id)} />
                </span>
                <span className="post-card__type-label">{drawerLabel}</span>
              </div>
            )}
            {post.post_type === 'video' && <span className="post-card__play" aria-hidden="true">▶</span>}
          </div>
          <div className="post-card__body">
            {drawerLabel && <p className="post-card__drawer kicker">{drawerLabel}</p>}
            {!post.hide_title && <h3 className="post-card__title">{post.title}</h3>}
            <p className="post-card__meta">
              <span>{post.like_count ?? 0} likes</span>
              <span aria-hidden="true"> · </span>
              <span>{post.comment_count ?? 0} comments</span>
              {post.poll_options && post.poll_options.length > 0 && (
                <>
                  <span aria-hidden="true"> · </span>
                  <span>Poll</span>
                </>
              )}
              {post.event && (
                <>
                  <span aria-hidden="true"> · </span>
                  <span>Event</span>
                </>
              )}
              {post.auction && (
                <>
                  <span aria-hidden="true"> · </span>
                  <span>Auction</span>
                </>
              )}
            </p>
          </div>
        </>
      )}

      {isText && (
        <div className="post-card__body post-card__body--text">
          <p className="post-card__meta">
            <span>{post.like_count ?? 0} likes</span>
            <span aria-hidden="true"> · </span>
            <span>{post.comment_count ?? 0} comments</span>
          </p>
        </div>
      )}
    </Link>
  );
}
